import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    type AdSlotProps,
    type AdSlotRenderEvent,
    DFPManager,
    type TargetingArguments
} from 'react-dfp';

import type {
    Contract
} from '../types';

import type {
    Organization
} from '@types';

type PublicationAdvertisementsOptions = {
    contractsList: Contract[] | undefined;
    featuredSlot: AdSlotProps;
    isReady: boolean;
    organizationDetails: Pick<Organization, 'salesforce_id' | 'tags' | 'physical_city' | 'physical_state' | 'physical_postal_code'> | undefined;
    sessionKey: string;
    wcSponsorSlot: AdSlotProps;
};

type PublicationAdvertisements = {
    isFeaturedAdEmpty: boolean;
    isWCSponsorAdEmpty: boolean;
    onSlotRegister: NonNullable<AdSlotProps['onSlotRegister']>;
    onSlotRender: NonNullable<AdSlotProps['onSlotRender']>;
    targetingArguments: TargetingArguments;
};

type EmptyAdvertisements = {
    sessionKey: string;
    slots: Record<string, boolean>;
};

const usePublicationAdvertisements = (options: PublicationAdvertisementsOptions): PublicationAdvertisements => {
    const {
        contractsList,
        featuredSlot,
        isReady,
        organizationDetails,
        sessionKey,
        wcSponsorSlot
    } = options;
    const requestedSlots = useRef(new WeakSet<googletag.Slot>());
    const [
        emptyAdvertisements,
        setEmptyAdvertisements
    ] = useState<EmptyAdvertisements>({
        sessionKey,
        slots: {}
    });

    const targetingArguments = useMemo((): TargetingArguments => {
        if (!organizationDetails || !contractsList) {
            return {};
        }

        return {
            Category: organizationDetails.tags,
            Church_ID: contractsList.map((contract) => String(contract.contract_id)).filter(Boolean),
            City: organizationDetails.physical_city ?? '',
            Salesforce_Id: organizationDetails.salesforce_id,
            State: organizationDetails.physical_state ?? '',
            Zip: organizationDetails.physical_postal_code ?? ''
        };
    }, [
        organizationDetails,
        contractsList
    ]);

    const onSlotRegister = useCallback((): void => {
        // Registration runs before the provider loads slots. This also replaces
        // inherited page targeting when GPT was already enabled on another route.
        DFPManager.setTargetingArguments(targetingArguments);
    }, [
        targetingArguments
    ]);

    const onSlotRender = useCallback((renderEvent: AdSlotRenderEvent): void => {
        const {
            event, slotId
        } = renderEvent;

        // We ignore late responses from destroyed slots, including reused DOM IDs.
        if (DFPManager.getRegisteredSlots()[slotId]?.gptSlot !== event.slot) {
            return;
        }

        setEmptyAdvertisements((previous) => ({
            sessionKey,
            slots: {
                ...(previous.sessionKey === sessionKey ? previous.slots : {}),
                [slotId]: event.isEmpty
            }
        }));
    }, [
        sessionKey
    ]);

    useEffect(() => {
        if (!isReady || !organizationDetails || !contractsList || !featuredSlot.slotId || !wcSponsorSlot.slotId) {
            return undefined;
        }

        const slotIDs = [
            featuredSlot.slotId,
            wcSponsorSlot.slotId
        ];
        let cancelled = false;
        let attempts = 0;
        let retryTimer: ReturnType<typeof setTimeout> | undefined;

        const tryRefresh = async (): Promise<void> => {
            try {
                const googleTag = await DFPManager.getGoogletag();

                if (cancelled) {
                    return;
                }

                googleTag.cmd.push(() => {
                    if (cancelled) {
                        return;
                    }

                    const registeredSlots = DFPManager.getRegisteredSlots();
                    const activeSlots = googleTag.pubads().getSlots();
                    const slots = slotIDs.flatMap((slotID) => {
                        const slot = registeredSlots[slotID]?.gptSlot;

                        return slot && activeSlots.includes(slot) && document.getElementById(slotID) ? [
                            slot
                        ] : [];
                    });

                    if (slots.length !== slotIDs.length) {
                        if (attempts < 20) {
                            attempts += 1;
                            retryTimer = setTimeout(tryRefresh, 200);
                        }

                        return;
                    }

                    // A prior route may have enabled GPT before disableInitialLoad could
                    // take effect. In that case display() already requests these ads.
                    if (!googleTag.pubads().isInitialLoadDisabled()) {
                        return;
                    }

                    const pendingSlots = slots.filter((slot) => !requestedSlots.current.has(slot));

                    if (!pendingSlots.length) {
                        return;
                    }

                    // Empty slot-level values must not inherit another organization's
                    // page-level targeting from the shared GPT service.
                    Object.keys(targetingArguments).forEach((key) => googleTag.pubads().clearTargeting(key));

                    pendingSlots.forEach((slot) => {
                        slot.clearTargeting();
                        Object.entries(targetingArguments).forEach((entry) => {
                            const [
                                key,
                                value
                            ] = entry;

                            slot.setTargeting(key, value);
                        });
                        requestedSlots.current.add(slot);
                    });

                    // We request only these live slot instances, once. Keeping the guard and
                    // refresh in the same GPT command avoids another asynchronous manager queue
                    // refreshing replacement slots after navigation or a breakpoint change.
                    googleTag.pubads().refresh(pendingSlots);
                });
            } catch {
                // Ads must not prevent publication viewing when GPT is unavailable.
            }
        };

        // We defer until the provider has configured GPT and the child slots have mounted.
        retryTimer = setTimeout(tryRefresh, 0);

        return () => {
            cancelled = true;
            clearTimeout(retryTimer);
        };
    }, [
        contractsList,
        featuredSlot.slotId,
        isReady,
        organizationDetails,
        sessionKey,
        targetingArguments,
        wcSponsorSlot.slotId
    ]);

    const emptySlots = emptyAdvertisements.sessionKey === sessionKey ? emptyAdvertisements.slots : {};

    return {
        isFeaturedAdEmpty: !!emptySlots[featuredSlot.slotId ?? ''],
        isWCSponsorAdEmpty: !!emptySlots[wcSponsorSlot.slotId ?? ''],
        onSlotRegister,
        onSlotRender,
        targetingArguments
    };
};

export default usePublicationAdvertisements;
